import React, { useState, useEffect } from 'react';
import { ExternalLink, Briefcase, GraduationCap, Users } from 'lucide-react';
import { EditableContent } from '../components/EditableContent';
import { useEditMode } from '../hooks/useEditMode';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const Careers = () => {
  const { isEditMode } = useEditMode();
  const [jobBoards, setJobBoards] = useState([
    {
      name: 'JobUp',
      url: 'https://www.jobup.ch/',
      description: 'Le plus grand portail d\'emploi en Suisse romande',
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/jobs/',
      description: 'Réseau professionnel et offres d\'emploi',
    },
    {
      name: 'Indeed',
      url: 'https://ch.indeed.com/',
      description: 'Moteur de recherche d\'emploi mondial',
    },
  ]);

  const sections = [
    {
      icon: Briefcase,
      title: 'Opportunités Diverses',
      description: 'Le secteur de l\'assurance offre une grande variété de postes et de perspectives d\'évolution.',
    },
    {
      icon: GraduationCap,
      title: 'Formation Continue',
      description: 'Des possibilités de formation continue pour développer vos compétences.',
    },
    {
      icon: Users,
      title: 'Réseau Professionnel',
      description: 'Un réseau actif de professionnels pour partager expériences et opportunités.',
    },
  ];

  useEffect(() => {
    const fetchJobBoards = async () => {
      try {
        const docRef = doc(db, 'content', 'job_boards');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.boards) {
            setJobBoards(data.boards);
          }
        }
      } catch (error) {
        console.error('Error fetching job boards:', error);
      }
    };

    fetchJobBoards();
  }, []);

  const handleUrlUpdate = async (index: number, newUrl: string) => {
    try {
      const updatedBoards = [...jobBoards];
      updatedBoards[index] = {
        ...updatedBoards[index],
        url: newUrl
      };

      await setDoc(doc(db, 'content', 'job_boards'), {
        boards: updatedBoards,
        updatedAt: new Date()
      });

      setJobBoards(updatedBoards);
    } catch (error) {
      console.error('Error updating URL:', error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">
        <EditableContent
          section="careers"
          contentKey="main-title"
          defaultValue="Carrière & Opportunités"
        />
      </h1>

      {/* Secteur de l'Assurance */}
      <section className="mb-16">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          <EditableContent
            section="careers"
            contentKey="sector-title"
            defaultValue="Le Secteur de l'Assurance"
          />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                <Icon className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  <EditableContent
                    section="careers"
                    contentKey={`section-${index}-title`}
                    defaultValue={section.title}
                  />
                </h3>
                <div className="text-gray-600">
                  <EditableContent
                    section="careers"
                    contentKey={`section-${index}-description`}
                    defaultValue={section.description}
                    type="textarea"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Offres d'Emploi */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          <EditableContent
            section="careers"
            contentKey="jobs-title"
            defaultValue="Trouver un Emploi"
          />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {jobBoards.map((board, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  <EditableContent
                    section="careers"
                    contentKey={`jobboard-${index}-name`}
                    defaultValue={board.name}
                  />
                </h3>
              </div>
              <div className="space-y-4">
                <div className="text-gray-600">
                  <EditableContent
                    section="careers"
                    contentKey={`jobboard-${index}-description`}
                    defaultValue={board.description}
                    type="textarea"
                  />
                </div>
                {isEditMode ? (
                  <div className="text-sm text-gray-500">
                    <EditableContent
                      section="careers"
                      contentKey={`jobboard-${index}-url`}
                      defaultValue={board.url}
                      onSave={(newValue) => handleUrlUpdate(index, newValue)}
                    />
                  </div>
                ) : (
                  <a
                    href={board.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    Visiter le site
                    <ExternalLink className="h-4 w-4 ml-1" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Careers;